import { useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal/Modal";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import {
  mediaId,
  useCreateMedia,
  useMedia,
  useUpdateProductGallery,
  type MediaItem,
  type ProductRecord,
} from "@/lib/queries";
import styles from "./ProductDetail.module.css";

/** Server-side cap (MAX_GALLERY in api/products.ts). Kept in sync here so the
 *  UI blocks adding past it instead of silently dropping on save. */
const MAX_LINKED = 12;

/**
 * Detail view for an imported product. Opened by clicking a product card on the
 * Products screen (replacing the old bounce straight to the store). Shows the
 * imported info — gallery, price, stock, description, tags, collections, source
 * + last-synced — with the store link kept as a labelled footer action.
 *
 * The imported fields are view-only (the store is the source of truth). The one
 * editable thing is the **linked media gallery** (`galleryMediaIds`): editors
 * can link Media Library photos to this product or upload a new one — these are
 * the photos the generator pulls as references. Linked photos also appear in
 * the gallery above.
 */
export function ProductDetailModal({
  product,
  ws,
  brand,
  canEdit,
  onClose,
}: {
  product: ProductRecord | null;
  ws: string | undefined;
  brand: string | undefined;
  canEdit: boolean;
  onClose: () => void;
}) {
  const [active, setActive] = useState(0);
  const [linked, setLinked] = useState<string[]>(product?.galleryMediaIds ?? []);
  const [picking, setPicking] = useState(false);

  const mediaQuery = useMedia(ws, brand);
  const save = useUpdateProductGallery(ws, brand);
  const upload = useCreateMedia(ws, brand);

  const mediaById = useMemo(() => {
    const m = new Map<string, MediaItem>();
    for (const it of mediaQuery.data?.media ?? []) m.set(mediaId(it), it);
    return m;
  }, [mediaQuery.data]);

  if (!product) return null;

  const catalogImages = product.sourceImageUrls ?? [];
  const linkedItems = linked
    .map((id) => mediaById.get(id))
    .filter((x): x is MediaItem => !!x);
  const linkedUrls = linkedItems.map((m) => m.url).filter((u): u is string => !!u);

  // The gallery shows catalog images first, then linked photos.
  const galleryImages = [...catalogImages, ...linkedUrls];
  const activeIdx = galleryImages.length ? Math.min(active, galleryImages.length - 1) : 0;
  const activeImg = galleryImages[activeIdx];

  const saving = save.isPending || upload.isPending;
  const atCap = linked.length >= MAX_LINKED;

  // Library images not already linked (and not videos) — the "add" picker pool.
  const available = (mediaQuery.data?.media ?? []).filter(
    (m) => m.mime.startsWith("image/") && !linked.includes(mediaId(m)),
  );

  function persist(next: string[]) {
    setLinked(next); // optimistic
    save.mutate(
      { handle: product!.handle, galleryMediaIds: next },
      { onSuccess: (res) => setLinked(res.product.galleryMediaIds ?? []) },
    );
  }

  function addLinked(id: string) {
    if (linked.includes(id) || linked.length >= MAX_LINKED) return;
    persist([...linked, id]);
  }

  function removeLinked(id: string) {
    persist(linked.filter((x) => x !== id));
  }

  function onUploadChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked later
    if (!file || atCap) return;
    upload.mutate(
      { file, purpose: "product", description: product!.title },
      { onSuccess: ({ media }) => persist([...linked, mediaId(media)]) },
    );
  }

  return (
    <Modal
      open
      title={product.title}
      onClose={onClose}
      footer={
        <>
          {product.url && (
            <Btn
              href={product.url}
              variant="secondary"
              iconRight={<Icon name="arrow-up-right" size={13} />}
            >
              View on website
            </Btn>
          )}
          <Btn variant="primary" onClick={onClose}>
            Close
          </Btn>
        </>
      }
    >
      <div className={styles.detail}>
        {/* Gallery (catalog images + linked photos) */}
        <div className={styles.gallery}>
          <div className={styles.heroWrap}>
            {activeImg ? (
              <img className={styles.hero} src={activeImg} alt={product.title} />
            ) : (
              <span className={styles.heroEmpty}>
                <Icon name="image" size={28} />
                <span>No images yet</span>
              </span>
            )}
          </div>
          {galleryImages.length > 1 && (
            <div className={styles.thumbs}>
              {galleryImages.map((url, i) => (
                <button
                  key={`${url}#${i}`}
                  type="button"
                  className={`${styles.thumb} ${i === activeIdx ? styles.thumbOn : ""}`}
                  onClick={() => setActive(i)}
                  aria-label={`Image ${i + 1}`}
                  aria-pressed={i === activeIdx}
                >
                  <img src={url} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Facts */}
        <div className={styles.facts}>
          <div className={styles.priceRow}>
            {product.priceCents != null && (
              <span className={styles.price}>
                {formatPrice(product.priceCents, product.currency)}
              </span>
            )}
            <span className={product.available ? styles.inStock : styles.outStock}>
              {product.available ? "In stock" : "Out of stock"}
            </span>
          </div>

          {product.description && (
            <p className={styles.description}>{product.description}</p>
          )}

          {product.collections?.length > 0 && (
            <div className={styles.metaBlock}>
              <span className={styles.metaLabel}>Collections</span>
              <div className={styles.chips}>
                {product.collections.map((c) => (
                  <span key={c} className={styles.chip}>{c}</span>
                ))}
              </div>
            </div>
          )}

          {product.tags?.length > 0 && (
            <div className={styles.metaBlock}>
              <span className={styles.metaLabel}>Tags</span>
              <div className={styles.chips}>
                {product.tags.map((t) => (
                  <span key={t} className={styles.chip}>{t}</span>
                ))}
              </div>
            </div>
          )}

          <div className={styles.provenance}>
            <span className={styles.provItem}>
              <Icon name="store" size={13} /> {sourceLabel(product.source)}
            </span>
            {product.syncedAt && (
              <span className={styles.provItem}>
                <Icon name="calendar" size={13} /> Synced {formatDate(product.syncedAt)}
              </span>
            )}
          </div>
        </div>

        {/* Linked media gallery (editable) */}
        <div className={styles.linkSection}>
          <div className={styles.linkHead}>
            <span className={styles.metaLabel}>
              <Icon name="link" size={12} /> Linked photos
            </span>
            {saving && <span className={styles.saving}>Saving…</span>}
          </div>
          <p className={styles.linkHint}>
            Photos you link here are used as references when generating posts for
            this product.
          </p>

          {linkedItems.length === 0 ? (
            <p className={styles.linkEmpty}>
              {canEdit
                ? "No linked photos yet — link one from your library or upload a new one."
                : "No linked photos yet."}
            </p>
          ) : (
            <div className={styles.linkGrid}>
              {linkedItems.map((m) => {
                const id = mediaId(m);
                return (
                  <div key={id} className={styles.linkItem}>
                    {m.url ? (
                      <img src={m.url} alt={m.context ?? ""} loading="lazy" />
                    ) : (
                      <span className={styles.linkFallback}>
                        <Icon name="image" size={16} />
                      </span>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        className={styles.removeBtn}
                        onClick={() => removeLinked(id)}
                        aria-label="Unlink photo"
                      >
                        <Icon name="x" size={11} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {canEdit && (
            <>
              <div className={styles.linkActions}>
                <Btn
                  size="sm"
                  variant="secondary"
                  onClick={() => setPicking((v) => !v)}
                  disabled={atCap}
                  iconLeft={<Icon name="link" size={13} />}
                >
                  {picking ? "Done linking" : "Link from library"}
                </Btn>
                <label className={`${styles.uploadLabel} ${atCap ? styles.uploadDisabled : ""}`}>
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    disabled={atCap || saving}
                    onChange={onUploadChange}
                  />
                  <Icon name="image" size={13} /> Upload photo
                </label>
              </div>

              {atCap && (
                <p className={styles.capNote}>
                  Up to {MAX_LINKED} linked photos. Remove one to add another.
                </p>
              )}

              {picking && !atCap && (
                <div className={styles.picker}>
                  {available.length === 0 ? (
                    <p className={styles.linkEmpty}>
                      Nothing left to link. Upload a new photo, or add images in the
                      Media Library.
                    </p>
                  ) : (
                    available.map((m) => {
                      const id = mediaId(m);
                      return (
                        <button
                          key={id}
                          type="button"
                          className={styles.pickItem}
                          onClick={() => addLinked(id)}
                          aria-label="Link this photo"
                          disabled={saving}
                        >
                          {m.url ? (
                            <img src={m.url} alt={m.context ?? ""} loading="lazy" />
                          ) : (
                            <span className={styles.linkFallback}>
                              <Icon name="image" size={16} />
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              )}

              {(save.error || upload.error) && (
                <p className={styles.linkError}>
                  {save.error?.message ?? upload.error?.message}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function sourceLabel(source: ProductRecord["source"]): string {
  switch (source) {
    case "shopify":
      return "Shopify";
    case "woocommerce":
      return "WooCommerce";
    case "website":
      return "Website";
    case "manual":
      return "Manual";
    default:
      return source;
  }
}

function formatPrice(cents: number, currency?: string): string {
  const amount = cents / 100;
  try {
    return amount.toLocaleString(undefined, { style: "currency", currency: currency || "USD" });
  } catch {
    return `${amount.toFixed(2)} ${currency ?? ""}`.trim();
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
